import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import { LogoMark } from '@/components/layout/Logo'
import { UserMenu } from '@/components/layout/UserMenu'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  SkeletonRows,
} from '@/components/layout/ui'
import { invitationsApi } from '@/lib/api'
import { messageFor } from '@/lib/errors'
import { formatRelative, titleCase } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { useInvitationInbox } from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * `/invitations` — every pending invitation addressed to the signed-in account.
 *
 * It lives outside a workspace because the whole point is that the recipient
 * has no access to those workspaces yet. Acceptance goes by invitation id, so
 * no raw token ever has to be handed back to the browser.
 */
export default function InvitationsPage() {
  const { defaultOrganization } = useSession()
  const inbox = useInvitationInbox()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const accept = useMutation({
    mutationFn: (id: string) => invitationsApi.accept(id),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.session })
      await queryClient.invalidateQueries({ queryKey: queryKeys.invitationInbox })
      navigate(`/${result.organization.slug}`)
    },
  })

  const decline = useMutation({
    mutationFn: (id: string) => invitationsApi.decline(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.invitationInbox }),
  })

  return (
    <div className="min-h-dvh">
      <header className="flex h-14 items-center justify-between border-b border-ink-200 px-4 dark:border-ink-800">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Taskly">
          <LogoMark />
          <span className="text-sm font-semibold tracking-tight">Taskly</span>
        </Link>
        <UserMenu />
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10">
        <PageHeader
          title="Invitations"
          description="Workspaces that have invited this email address."
          action={
            defaultOrganization ? (
              <Link
                to={`/${defaultOrganization.slug}`}
                className="inline-flex h-10 items-center rounded-lg border border-ink-200 px-4 text-sm font-medium transition hover:bg-ink-50 dark:border-ink-700 dark:hover:bg-ink-800"
              >
                Back to {defaultOrganization.name}
              </Link>
            ) : null
          }
        />

        {accept.error ? (
          <Alert tone="danger" className="mb-4">
            {messageFor(accept.error)}
          </Alert>
        ) : null}

        <Card>
          <CardHeader title="Pending" description="Each link is single-use and expires." />
          {inbox.isPending ? (
            <div className="p-5">
              <SkeletonRows rows={2} />
            </div>
          ) : inbox.isError ? (
            <div className="p-5">
              <Alert tone="danger">{messageFor(inbox.error)}</Alert>
            </div>
          ) : inbox.data.length === 0 ? (
            <EmptyState
              title="No pending invitations"
              description="When someone invites you to a workspace, it appears here."
            />
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {inbox.data.map((invitation) => (
                <li key={invitation.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{invitation.organization.name}</p>
                    <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
                      {titleCase(invitation.role)} · expires {formatRelative(invitation.expires_at)}
                    </p>
                  </div>
                  <Badge tone="brand">{titleCase(invitation.role)}</Badge>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => decline.mutate(invitation.id)}
                      loading={decline.isPending && decline.variables === invitation.id}
                    >
                      Decline
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => accept.mutate(invitation.id)}
                      loading={accept.isPending && accept.variables === invitation.id}
                    >
                      Accept
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </main>
    </div>
  )
}
